import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

/**
 * Reusable signature-capture widget — draw / type / upload.
 *
 * Extracted from SignDocument.jsx so the same signing UI can be reused by
 * the Team Member Info form. Exposes an imperative ref API instead of
 * pushing state up on every change, which mirrors the original component's
 * behavior exactly: validity/data is only read when the caller asks for it
 * (at render time for the Submit button's `disabled`, and at submit time).
 *
 *   const sigRef = useRef(null);
 *   <SignaturePad ref={sigRef} />
 *   sigRef.current.canSubmit()      -> boolean
 *   sigRef.current.getMethod()      -> 'draw' | 'type' | 'upload'
 *   sigRef.current.getSignatureData() -> base64 image data URL or typed name
 */
const SignaturePad = forwardRef(function SignaturePad(_props, ref) {
  const [method, setMethod] = useState("draw"); // draw | type | upload
  const [typedName, setTypedName] = useState("");
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploadData, setUploadData] = useState("");

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
    hasDrawnRef.current = true;
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1f2937";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
  };

  const handleUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadData(reader.result);
      setUploadPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  useImperativeHandle(ref, () => ({
    canSubmit: () => {
      if (method === "draw") return hasDrawnRef.current;
      if (method === "type") return typedName.trim().length > 1;
      if (method === "upload") return !!uploadData;
      return false;
    },
    getMethod: () => method,
    getSignatureData: () => {
      if (method === "draw") return canvasRef.current.toDataURL("image/png");
      if (method === "type") return typedName.trim();
      if (method === "upload") return uploadData;
      return "";
    },
  }));

  return (
    <div>
      <h3 style={{ marginBottom: 8 }}>Sign Here</h3>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["draw", "type", "upload"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMethod(m)}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid #d1d5db", cursor: "pointer",
              background: method === m ? "#8b3db8" : "white",
              color: method === m ? "white" : "#374151",
              fontWeight: 600,
            }}
          >
            {m === "draw" ? "✍️ Draw" : m === "type" ? "⌨️ Type Name" : "📤 Upload Signature"}
          </button>
        ))}
      </div>

      {method === "draw" && (
        <div>
          <canvas
            ref={canvasRef}
            width={640}
            height={180}
            style={{ width: "100%", height: 180, border: "2px dashed #d1d5db", borderRadius: 8, touchAction: "none", background: "#fafafa" }}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
          />
          <button type="button" onClick={clearCanvas} style={{ marginTop: 8, padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: "white", cursor: "pointer" }}>
            Clear
          </button>
        </div>
      )}

      {method === "type" && (
        <div>
          <input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Type your full name"
            style={{ width: "100%", padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15, boxSizing: "border-box" }}
          />
          {typedName.trim() && (
            <p style={{ fontFamily: "cursive", fontStyle: "italic", fontSize: 28, marginTop: 12, color: "#1f2937" }}>
              {typedName}
            </p>
          )}
        </div>
      )}

      {method === "upload" && (
        <div>
          <input type="file" accept="image/*" onChange={handleUploadFile} />
          {uploadPreview && (
            <div style={{ marginTop: 12, padding: 10, border: "1px solid #d1d5db", borderRadius: 8, display: "inline-block" }}>
              <img src={uploadPreview} alt="Signature preview" style={{ maxHeight: 100, maxWidth: 300 }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
